import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Trophy, Flame, Zap, Crown, Shield, Target, Users,
  Star, Rocket, Award, Coins, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = 'aegispay_seen_badges';

const ICONS = { rocket: Rocket, coins: Coins, flame: Flame, zap: Zap, crown: Crown, users: Users, trophy: Trophy, shield: Shield, target: Target, star: Star };

const BadgeItem = ({ badge }) => {
  const Icon = ICONS[badge.icon] || Award;
  return (
    <Card className="glass-card border-l-4 border-l-amber-400" data-testid={`badge-${badge.id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="p-3 rounded-xl bg-amber-50"><Icon size={24} className="text-amber-600" /></div>
        <div className="flex-1"><p className="font-bold text-slate-900">{badge.name}</p><p className="text-xs text-slate-500">{badge.description}</p></div>
        <Badge className="bg-amber-100 text-amber-700 text-[9px]">Earned</Badge>
      </CardContent>
    </Card>
  );
};

const LeaderRow = ({ item, rank, type }) => {
  const pos = rank === 0 ? '1st' : rank === 1 ? '2nd' : rank === 2 ? '3rd' : `${rank + 1}`;
  const highlight = item.is_me;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${highlight ? 'bg-[#0B1F3A]/5 border-2 border-[#0B1F3A]/20' : 'bg-slate-50'}`}>
      <span className={`text-lg font-black w-8 text-center ${rank < 3 ? 'text-amber-500' : 'text-slate-400'}`}>{pos}</span>
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-800">{item.user_name} {highlight && <span className="text-xs text-[#2272C5]">(You)</span>}</p>
        {type === 'streak' && <p className="text-[10px] text-slate-500">{item.total_contributions} contributions</p>}
      </div>
      <div className="flex items-center gap-1">
        {type === 'streak' ? <Flame size={14} className="text-orange-500" /> : <Star size={14} className="text-emerald-500" />}
        <span className="text-sm font-bold text-slate-900">{type === 'streak' ? item.streak : item.trust_score}</span>
      </div>
    </div>
  );
};

const Achievements = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('badges');
  const [newlyUnlocked, setNewlyUnlocked] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/engagement/profile`, { headers: getAuthHeaders() }),
      axios.get(`${API_URL}/api/engagement/leaderboard`, { headers: getAuthHeaders() }),
    ]).then(([p, l]) => {
      setProfile(p.data);
      setLeaderboard(l.data);

      // Detect newly-unlocked badges vs. last-seen set
      try {
        const earned = (p.data?.badges || []).map((b) => b.id);
        const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        const fresh = (p.data?.badges || []).filter((b) => !seen.includes(b.id));
        if (fresh.length > 0) {
          setNewlyUnlocked(fresh);
          confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 } });
        }
        localStorage.setItem(SEEN_KEY, JSON.stringify(earned));
      } catch {}
    })
      .catch(() => toast.error('Failed'))
      .finally(() => setLoading(false));
  }, []);

  const dismissUnlocked = () => setNewlyUnlocked([]);

  if (loading) return (
    <div className="min-h-screen bg-stone-50 pb-24"><div className="app-container px-4 pt-6 space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-48 rounded-2xl" /></div><BottomNav /></div>
  );

  const badges = profile?.badges || [];
  const streakLeaders = leaderboard?.streak_leaders || [];
  const trustLeaders = leaderboard?.trust_leaders || [];

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="achievements-page">
      {/* Badge unlock celebration overlay */}
      {newlyUnlocked.length > 0 && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm px-6"
          data-testid="badge-unlock-modal"
          onClick={dismissUnlocked}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-gradient-to-b from-amber-300 via-amber-200 to-amber-50 p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-3">
              <div className="p-4 rounded-full bg-white shadow-lg animate-bounce">
                <Sparkles size={36} className="text-amber-600" />
              </div>
            </div>
            <p className="text-xs font-black uppercase tracking-widest text-amber-800">New Badge{newlyUnlocked.length > 1 ? 's' : ''} Unlocked</p>
            <div className="mt-4 space-y-3">
              {newlyUnlocked.map((b) => {
                const I = ICONS[b.icon] || Award;
                return (
                  <div key={b.id} className="bg-white/80 rounded-2xl p-4 flex items-center gap-3" data-testid={`unlocked-badge-${b.id}`}>
                    <div className="p-2.5 rounded-xl bg-amber-100"><I size={22} className="text-amber-700" /></div>
                    <div className="flex-1 text-left">
                      <p className="font-black text-[#0B1F3A]">{b.name}</p>
                      <p className="text-xs text-slate-600 font-medium">{b.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              onClick={dismissUnlocked}
              className="mt-5 w-full h-12 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-bold"
              data-testid="unlock-modal-close"
            >
              Awesome!
            </Button>
          </div>
        </div>
      )}

      <div className="app-container px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white shadow-sm"><ArrowLeft size={20} className="text-slate-700" /></button>
          <div><h1 className="text-xl font-bold text-slate-900">Achievements</h1><p className="text-xs text-slate-500">{profile?.title}</p></div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="glass-card text-center"><CardContent className="p-3"><Flame size={20} className="mx-auto text-orange-500 mb-1" /><p className="text-xl font-black text-slate-900">{profile?.current_streak || 0}</p><p className="text-[10px] text-slate-500">Streak</p></CardContent></Card>
          <Card className="glass-card text-center"><CardContent className="p-3"><Award size={20} className="mx-auto text-amber-500 mb-1" /><p className="text-xl font-black text-slate-900">{badges.length}/{profile?.total_badges || 10}</p><p className="text-[10px] text-slate-500">Badges</p></CardContent></Card>
          <Card className="glass-card text-center"><CardContent className="p-3"><Shield size={20} className="mx-auto text-emerald-500 mb-1" /><p className="text-xl font-black text-slate-900">{profile?.trust_score || 0}</p><p className="text-[10px] text-slate-500">Trust</p></CardContent></Card>
        </div>

        <div className="flex gap-2 mb-4">
          <Button size="sm" variant={tab === 'badges' ? 'default' : 'outline'} className={`flex-1 text-xs rounded-xl ${tab === 'badges' ? 'bg-[#0B1F3A] text-white' : 'text-[#0B1F3A]'}`} onClick={() => setTab('badges')} data-testid="tab-badges">Badges</Button>
          <Button size="sm" variant={tab === 'leaderboard' ? 'default' : 'outline'} className={`flex-1 text-xs rounded-xl ${tab === 'leaderboard' ? 'bg-[#0B1F3A] text-white' : 'text-[#0B1F3A]'}`} onClick={() => setTab('leaderboard')} data-testid="tab-leaderboard">Leaderboard</Button>
        </div>

        {tab === 'badges' && (
          <div className="space-y-3" data-testid="badges-list">
            {badges.length > 0 ? badges.map(b => <BadgeItem key={b.id} badge={b} />) : (
              <Card className="glass-card"><CardContent className="p-8 text-center"><Trophy size={40} className="mx-auto text-slate-300 mb-2" /><p className="text-sm text-slate-500">No badges yet. Start contributing!</p></CardContent></Card>
            )}
            <p className="text-center text-xs text-slate-400 mt-4">{badges.length} of {profile?.total_badges || 10} earned</p>
          </div>
        )}

        {tab === 'leaderboard' && (
          <div className="space-y-4" data-testid="leaderboard">
            <Card className="glass-card">
              <CardHeader className="pb-2"><CardTitle className="text-base text-[#0B1F3A] flex items-center gap-2"><Flame size={16} className="text-orange-500" />Most Consistent</CardTitle></CardHeader>
              <CardContent>
                {streakLeaders.length > 0 ? <div className="space-y-2">{streakLeaders.map((l, i) => <LeaderRow key={i} item={l} rank={i} type="streak" />)}</div> : <p className="text-center text-sm text-slate-400 py-4">No data yet</p>}
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardHeader className="pb-2"><CardTitle className="text-base text-[#0B1F3A] flex items-center gap-2"><Shield size={16} className="text-emerald-500" />Highest Trust</CardTitle></CardHeader>
              <CardContent>
                {trustLeaders.length > 0 ? <div className="space-y-2">{trustLeaders.map((l, i) => <LeaderRow key={i} item={l} rank={i} type="trust" />)}</div> : <p className="text-center text-sm text-slate-400 py-4">No data yet</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default Achievements;
